import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, Checkbox} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { MdFlag } from 'react-icons/md';
import { SportBrand, UpsertSportBrand } from '@/models/sport-brand.model';
import { upsertSportBrand } from '@/services/sport-brand.service';

const schema = Schema.Model<UpsertSportBrand>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  coverImageUrl: Schema.Types.StringType(),
  haveShoe: Schema.Types.BooleanType().isRequired('This field is required.'),
  haveCloth: Schema.Types.BooleanType().isRequired('This field is required.'),
});


const emptyModel: UpsertSportBrand = {
  id: undefined,
  name: undefined,
  coverImageUrl: undefined,
  haveShoe: false,
  haveCloth: false
};

interface SportBrandFormProps{
  formObject?: SportBrand;
  pageState: PageState
};

const SportBrandForm: React.FC<SportBrandFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertSportBrand>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      const castedModel: UpsertSportBrand = { 
        ...formObject, 
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertSportBrand(formValue);
      if (result.success) {
        nav('/sport-brands');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
     <Form name='sport-brand-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Cover Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="coverImageUrl"
                  defaultIcon={MdFlag}
                  height="250px"
                  url={formValue.coverImageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.SportBrand}
                  onChange={url => setFormValue(pre => ({...pre, coverImageUrl: url}))}              
                />
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" value={formValue.name ?? ''}/>
              </Form.Group>
            </Col>
          </Row> 
          <Row justifyContent="center">
            <Col span={{xs:24, xl:6}} >
              <Form.Group>
                <Form.Label>Have Shoe</Form.Label>
                <Form.Control name="haveShoe" accepter={Checkbox}
                  checked={!!formValue.haveShoe} 
                  onChange={(val, chk) => setFormValue(pre => ({ ...pre, haveShoe: chk })) } 
                />                   
              </Form.Group>                 
            </Col>
            <Col span={{xs:24, xl:6}} >
              <Form.Group>
                <Form.Label>Have Cloth</Form.Label>
                <Form.Control name="haveCloth" accepter={Checkbox}
                  checked={!!formValue.haveCloth} 
                  onChange={(val, chk) => setFormValue(pre => ({ ...pre, haveCloth: chk })) } 
                />                   
              </Form.Group>                 
            </Col>
          </Row>
        </Grid>
      </Form>

      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default SportBrandForm;

